import React from "react";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { PermissionType } from "../../../Core/Api/Types/permission-type";
import Modal, { CommonModalProps } from "..";
import { Stack, Button, CircularProgress } from "@mui/material";
import AssetAndChildrenCard from "../../Access/Shared/asset-and-children-card";
import PermissionsFormSection from "../../Access/Shared/permissions-form-section";
import { assetService } from "../../../Core/Api/services";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { useTranslation } from "react-i18next";

export type ModalUpdateDefaultPermissionsElementProps = {
  asset: DbAssetObjectInfos;
  selectedPermissions: PermissionType[];
  onSuccess: () => void;
};

export type ModalUpdateDefaultPermissionsProps = CommonModalProps<ModalUpdateDefaultPermissionsElementProps>;

const ModalUpdateDefaultPermissions = React.forwardRef<any, ModalUpdateDefaultPermissionsProps>((props, ref) => {
  const { t } = useTranslation();
  const { asset, selectedPermissions: _selectedPermissions, onSuccess, closeModal } = props;

  const enqueueSnackbar = useCustomSnackbar();

  const [isWaitingCall, setIsWaitingCall] = React.useState(false);
  const [selectedPermissions, setSelectedPermissions] = React.useState<PermissionType[]>(_selectedPermissions);

  const handlePermissionClick = (permission: PermissionType) => {
    if (selectedPermissions.includes(permission)) {
      setSelectedPermissions(selectedPermissions.filter((p) => p !== permission));
    } else {
      setSelectedPermissions([...selectedPermissions, permission]);
    }
  };

  const handleConfirmClick = () => {
    setIsWaitingCall(true);
    assetService
      .SetDefaultPermissions({ objectId: asset.id, defaultPermissions: selectedPermissions })
      .then((reponse) => {
        if (reponse.success) {
          onSuccess();
          enqueueSnackbar(t("alert:success.defaultPermissionsUpdated"), "success");
          closeModal();
        } else {
          enqueueSnackbar(reponse.message || t("alert:error.oopsSomethingWentWrongWithoutDot"), "error");
        }
      })
      .finally(() => setIsWaitingCall(false));
  };

  return (
    <Modal
      title={t("modal:title.overrideDefaultPermissions")}
      reference={ref}
      closeModal={closeModal}
      width={600}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={closeModal}>{t("common:cancel")}</Button>
          <Button variant="contained" onClick={handleConfirmClick}>
            {isWaitingCall ? <CircularProgress size={20} color="secondary" /> : t("button:setDefaultPermissions")}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={2}>
        <AssetAndChildrenCard asset={asset} />
        <PermissionsFormSection
          selectedPermissions={selectedPermissions}
          handlePermissionClick={handlePermissionClick}
          variant="accent"
        />
      </Stack>
    </Modal>
  );
});

export default ModalUpdateDefaultPermissions;
